import CheckCircleOutline from '@material-ui/icons/CheckCircleOutlineOutlined'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import SyncIcon from '@material-ui/icons/SyncOutlined'
import { useActionsListener } from 'ducks/action_listener'
import Button from 'elements/button/Button'
import CustomSelectField from 'elements/field/CustomSelectField'
import { FormApi } from 'final-form'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { Alert, InputAdornment, MenuItem, Switch, TextField, Tooltip } from 'opensolar-ui'
import { useTranslate, useTranslateParse } from 'ra-core'
import { FC, Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import { projectErrorSelector } from 'reducer/project/projectErrorsReducer'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { IntegrationJsonType, StudioSystemType } from 'types/global'
import {
  CheckboxGroupInput,
  DropdownInput,
  InputValue,
  IntegrationData,
  MountingFieldFetchConfig,
  NativeMountingID,
  NativeMountingInputs,
  PartnerAccessoriesID,
  RoofHookCalculationMethodOption,
  TextFieldInput,
} from 'types/mounting'
import { ActionType } from 'types/tracking'
import { useDebouncedCallback } from 'util/Debounce'
import { generateUUID } from 'util/misc'
import { getPanelLayoutDimensions } from './compatibility'
import { partnerAccessoriesIDs } from './constants'
import { getDropdownChangeHandler } from './dropdownChangeHandlers'
import { getSolarNestMaxWindSnow } from './mounting_systems/solarNestWindSnowMax'
import { fetchDlubalUKData, requestAutoFetchDlubalUKDataIfNeeded } from './mounting_systems/ukDlubal'
import { getMountingFetchErrorMessage } from './mountingFetchErrorMessage'
import getNativeMountingSystems from './mountingSystems'
import { useMountingStaleInputBorderStyles } from './mountingStaleInputBorder'
import { partnerAccessories } from './partnerAccessories'

/** Van der Valk dropdowns use full container width (max 230px); legacy text fields used 215px + 15px margin. */
const VDV_MOUNTING_INPUT_MAX_WIDTH_PX = 230
/** Matches CustomSelectField formControl marginTop in mounting dropdowns. */
const VDV_MOUNTING_FIELD_MARGIN_TOP_PX = 20

function isOutlineWarningField(variableName: string, outlineWarningFieldNames?: readonly string[]): boolean {
  return outlineWarningFieldNames?.includes(variableName) ?? false
}

const boldTooltipLabel = (text: string) => <strong>{text}</strong>

const DEFAULT_FAILED_FETCH_TOOLTIP = 'External data fetch failed. You can retry or enter values manually.'

function renderMountingTooltipMessage(
  tooltipKey: string,
  translate: (key: string) => string,
  translateParse: (key: string, placeholders: Record<string, (text: string) => ReactNode>) => ReactNode
): ReactNode {
  const translated = translate(tooltipKey)
  // Numbered tags support multiple bold segments; <strong> only supports one pair per string.
  if (/<\d+>/.test(translated)) {
    return translateParse(tooltipKey, { 1: boldTooltipLabel, 2: boldTooltipLabel, 3: boldTooltipLabel })
  }
  if (/<strong>/.test(translated)) {
    return translateParse(tooltipKey, { strong: boldTooltipLabel })
  }
  return translated
}

function isVanDerValkMountingName(mountingName: NativeMountingID | PartnerAccessoriesID): boolean {
  return mountingName === 'vanDerValk'
}

/** Mounting tab text fields that match dropdown width/spacing (no legacy 215px + 15px margin). */
function usesCompactMountingTextFieldLayout(mountingName: NativeMountingID | PartnerAccessoriesID): boolean {
  return isVanDerValkMountingName(mountingName) || mountingName === 'snapnrackUltraRail'
}

function mountingTextFieldContainerStyle(
  mountingName: NativeMountingID | PartnerAccessoriesID,
  fullWidth = false
) {
  if (fullWidth) {
    return {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 8,
      width: '100%',
    }
  }
  if (usesCompactMountingTextFieldLayout(mountingName)) {
    return {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '10px',
      width: '100%',
      maxWidth: VDV_MOUNTING_INPUT_MAX_WIDTH_PX,
      marginTop: VDV_MOUNTING_FIELD_MARGIN_TOP_PX,
    }
  }
  return { display: 'flex', flexDirection: 'column' as const, gap: '10px' }
}

function mountingTextFieldStyle(mountingName: NativeMountingID | PartnerAccessoriesID, fullWidth = false) {
  if (fullWidth) {
    return { flex: '1 1', width: '100%' }
  }
  if (usesCompactMountingTextFieldLayout(mountingName)) {
    return { flex: '1 1', width: '100%', maxWidth: VDV_MOUNTING_INPUT_MAX_WIDTH_PX }
  }
  return { flex: '1 1', width: 215, margin: 15 }
}

function mountingTextFieldButtonStyle(mountingName: NativeMountingID | PartnerAccessoriesID, fullWidth = false) {
  if (fullWidth) {
    return {
      height: 'fit-content',
      width: '100%',
      alignSelf: 'flex-start' as const,
    }
  }
  if (usesCompactMountingTextFieldLayout(mountingName)) {
    return {
      height: 'fit-content',
      width: '100%',
      maxWidth: VDV_MOUNTING_INPUT_MAX_WIDTH_PX,
      alignSelf: 'flex-start' as const,
    }
  }
  return {
    height: 'fit-content',
    width: '215px',
    alignSelf: 'flex-start' as const,
    marginLeft: '15px',
  }
}

function mountingFetchDropdownFieldStyle(mountingName: NativeMountingID | PartnerAccessoriesID) {
  if (isVanDerValkMountingName(mountingName)) {
    return { flex: '1 1', width: '100%', maxWidth: VDV_MOUNTING_INPUT_MAX_WIDTH_PX }
  }
  return { flex: '1 1', width: 215, margin: 15 }
}

function mountingVanDerValkDropdownStyle() {
  return {
    flex: '1 1',
    width: '100%',
    maxWidth: VDV_MOUNTING_INPUT_MAX_WIDTH_PX,
  }
}

/** Fetch dropdown container — no marginTop for VDV; CustomSelectField formControl already adds 20px. */
function mountingFetchDropdownContainerStyle(mountingName: NativeMountingID | PartnerAccessoriesID) {
  if (isVanDerValkMountingName(mountingName)) {
    return {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '10px',
      width: '100%',
      maxWidth: VDV_MOUNTING_INPUT_MAX_WIDTH_PX,
    }
  }
  return { display: 'flex', flexDirection: 'column' as const, gap: '10px' }
}

export function getRoofHookCalculationMethodInputs(
  system: StudioSystemType,
  spacingDisabled?: boolean
): NativeMountingInputs {
  let defaultValue = 'basic'
  let roofHookOptions = [{ label: 'None', value: 'basic' }] as RoofHookCalculationMethodOption[]
  let dropdownInputs: DropdownInput[] = []
  let textFieldInputs: TextFieldInput[] = []

  if (window.AccountHelper.getOrgCountryIso2() === 'GB') {
    defaultValue = 'mcs'
    roofHookOptions.push({
      label: 'MCS',
      value: 'mcs',
    })
  }

  dropdownInputs.push({
    label: 'Roof hook loading calculation',
    variableName: 'roofHookCalculationMethod',
    default: defaultValue,
    options: roofHookOptions,
  })

  if (!spacingDisabled) {
    if (['US', 'CA'].includes(window.AccountHelper.getOrgCountryIso2())) {
      textFieldInputs.push({
        type: 'number',
        label: 'Mounting Spacing (inches)',
        variableName: 'mountingSpacingInches',
        default: 48,
        min: 16,
      })
    } else {
      textFieldInputs.push({
        type: 'number',
        label: 'Mounting Spacing',
        variableName: 'mountingSpacing',
        default: 800,
        min: 200,
      })
    }
  }

  return {
    dropdownInputs,
    textFieldInputs,
  }
}

const initMountingData = (system: StudioSystemType, mountingName: NativeMountingID | PartnerAccessoriesID) => {
  if (!system.integration_json) {
    system.integration_json = {} as IntegrationJsonType
  }
  if (!system.integration_json[mountingName]) {
    system.integration_json[mountingName] = {}
  }
}

export const syncGroundClearanceToPanelSettings = (system: StudioSystemType, valueInInches: number) => {
  if (isNaN(valueInInches)) return
  const valueInMeters = valueInInches / 39.3701
  system.moduleGrids().forEach((moduleGrid) => {
    if (moduleGrid.getGroundClearance() !== valueInMeters) {
      window.editor.execute(new window.SetPanelConfigurationCommand(moduleGrid, 'groundClearance', valueInMeters))
    }
  })
}

export const handleApplyMountingSetting = (key, value, uuid, mountingName: NativeMountingID | PartnerAccessoriesID) => {
  var system = window.editor.selectedSystem
  initMountingData(system, mountingName)
  if (system.integration_json && system.integration_json[mountingName] && mountingName && key) {
    if (system.integration_json[mountingName] === undefined) {
      system.integration_json[mountingName] = { [key]: value }
    } else {
      system.integration_json[mountingName] = { ...system.integration_json[mountingName], [key]: value }
    }
  }
  if (key === 'groundClearance' && mountingName === 'solarNestGroundMount') {
    syncGroundClearanceToPanelSettings(system, Number(value))
  }
  if (partnerAccessoriesIDs.includes(mountingName as PartnerAccessoriesID)) {
    window.editor.signals.objectChanged.dispatch(system, 'partner_accessories')
  } else {
    window.editor.signals.objectChanged.dispatch(system, 'mounting')
  }
}

const useStyles = makeOpenSolarStyles((theme) => ({
  switchWrapper: {
    display: 'flex',
    marginTop: '15px',
    maxWidth: '230px',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

type MountingInputsProps = {
  mountingName: NativeMountingID
  allowEdit: boolean
  afterAutoRacking?: ReactNode
  /** When true, only the auto-racking toggle is shown (Van der Valk until suitability is selected). */
  hideInputFields?: boolean
  /** Override the default Inputs component for system-specific rendering (e.g. FastRack). */
  FieldsComponent?: FC<InputsProps>
}

type PartnerAccessoryInputsProps = {
  partnerAccessory: PartnerAccessoriesID
  allowEdit: boolean
}

// Sometimes the input options change (eg. if the option is incompatible with other selections) so override the selected input if it is no longer an option
const saveDefaultIfOptionNoLongerExists = (form, system, mountingName) => {
  const mountingJson = system?.integration_json ? system?.integration_json[mountingName] : undefined
  const nativeMountingSystems = getNativeMountingSystems()
  const inputs = nativeMountingSystems[mountingName]?.getInputs(form, system)
  const dropdownInputs = [...(inputs?.dropdownInputs || []), ...(inputs?.trailingDropdownInputs || [])]
  if (dropdownInputs.length) {
    dropdownInputs.forEach((input) => {
      if (mountingJson && mountingJson[input.variableName]) {
        const selectedInputIsVisible = input.options.filter(
          (option) => option.value === mountingJson[input.variableName]
        ).length
        if (!selectedInputIsVisible) {
          // save the new default input value
          handleApplyMountingSetting(input.variableName, input.default, window.editor.selectedSystem.uuid, mountingName)
        }
      }
    })
  }
}

export const saveInputs = (form, system) => {
  const mountingName = system.mounting
  const nativeMountingSystems = getNativeMountingSystems()
  const inputs = nativeMountingSystems[mountingName]?.getInputs(form, system)
  const mountingJson = system?.integration_json ? system?.integration_json[mountingName] : undefined
  saveAllDefaultInputs(inputs, mountingJson, mountingName)
  saveDefaultIfOptionNoLongerExists(form, system, mountingName)
}

function flattenMountingInputs(
  inputs: NativeMountingInputs | undefined
): Array<DropdownInput | TextFieldInput | CheckboxGroupInput> {
  if (!inputs) {
    return []
  }
  return [
    ...(inputs.dropdownInputs || []),
    ...(inputs.textFieldInputsAfterAnchor || []),
    ...(inputs.textFieldInputs || []),
    ...(inputs.trailingDropdownInputs || []),
    ...(inputs.trailingTextFieldInputs || []),
    ...(inputs.checkboxGroupInputs || []),
  ]
}

function saveAllDefaultInputs(inputs: NativeMountingInputs | undefined, mountingJson, mountingName) {
  return flattenMountingInputs(inputs).forEach((input) => {
    saveDefaultInputState(input, mountingJson, mountingName)
  })
}

function saveDefaultInputState(input, mountingJson, mountingName) {
  if (typeof mountingJson === 'undefined' || typeof mountingJson[input.variableName] === 'undefined') {
    if (input.default === '' || input.default === null || input.default === undefined) {
      return
    }
    handleApplyMountingSetting(input.variableName, input.default, window.editor.selectedSystem.uuid, mountingName)
  }
}

function resetToDefaultValues(inputs: NativeMountingInputs, mountingSystemName) {
  const allInputs = flattenMountingInputs(inputs)
  allInputs.forEach((input) => {
    if (input.default === '' || input.default === null || input.default === undefined) {
      return
    }
    handleApplyMountingSetting(input.variableName, input.default, window.editor.selectedSystem.uuid, mountingSystemName)
  })
}

export type NativeMountingInputsFieldsProps = {
  inputs: NativeMountingInputs
  mountingJson: any
  allowEdit: boolean
  integrationName: NativeMountingID | PartnerAccessoriesID
  renderAfterTextField?: (variableName: string) => ReactNode
  renderAfterDropdown?: (variableName: string) => ReactNode
  renderFooter?: ReactNode
  outlineWarningFieldNames?: readonly string[]
}

export type InputsProps = {
  inputs: NativeMountingInputs
  mountingJson: any
  allowEdit: boolean
  integrationName: NativeMountingID | PartnerAccessoriesID
}

export const NativeMountingInputsFields: FC<NativeMountingInputsFieldsProps> = ({
  inputs,
  mountingJson,
  allowEdit,
  integrationName,
  renderAfterTextField,
  renderAfterDropdown,
  renderFooter,
  outlineWarningFieldNames,
}) => {
  const system = window.editor.selectedSystem
  const translate = useTranslate()
  const translateParse = useTranslateParse()
  const form = useForm()
  const staleInputBorderClasses = useMountingStaleInputBorderStyles()
  const dropdownInputs = inputs?.dropdownInputs
  const textFieldInputs = inputs?.textFieldInputs
  const trailingDropdownInputs = inputs?.trailingDropdownInputs
  const trailingTextFieldInputs = inputs?.trailingTextFieldInputs
  const roofType = form.getState().values.roof_type
  const roofTypeRef = useRef(roofType)
  const autoRackingEnabled = typeof mountingJson?.enabled !== 'undefined' ? mountingJson.enabled : false
  const projectErrors = useSelector(projectErrorSelector.getAllErrors)
  const hasMountingError = projectErrors.some(
    (error) =>
      error.systemId === system.uuid &&
      ['k2OnRoof', 'k2SimpleTilt'].includes(integrationName) &&
      error.category === 'mounting' &&
      error.source === 'plugin' &&
      (error.severity === 'warning' || error.severity === 'error')
  )
  useEffect(() => {
    if (roofTypeRef.current !== roofType && autoRackingEnabled) {
      resetToDefaultValues(inputs, integrationName)
      roofTypeRef.current = roofType
    }
  }, [roofType, autoRackingEnabled])

  const hasShownAboveMaxToast = useRef(false)
  useEffect(() => {
    if (integrationName !== 'solarNestRooftop' && integrationName !== 'solarNestGroundMount') return
    const maxes = getSolarNestMaxWindSnow(system)
    if (!maxes) {
      hasShownAboveMaxToast.current = false
      return
    }
    const currentWind = Number(mountingJson?.windSpeed)
    const currentSnow = Number(mountingJson?.designSnowLoad)
    const windAboveMax = typeof currentWind === 'number' && !Number.isNaN(currentWind) && currentWind > maxes.maxWindMph
    const snowAboveMax = typeof currentSnow === 'number' && !Number.isNaN(currentSnow) && currentSnow > maxes.maxSnowPsf
    if (windAboveMax || snowAboveMax) {
      if (!hasShownAboveMaxToast.current) {
        window.Designer?.showNotification(
          translate(
            "This location's wind and snow zones are above the maximum for this system and requires structural analysis, please contact PES/Nuance"
          ),
          'info'
        )
        hasShownAboveMaxToast.current = true
      }
    } else {
      hasShownAboveMaxToast.current = false
    }
  }, [integrationName, system, mountingJson?.windSpeed, mountingJson?.designSnowLoad, translate])

  const renderDropdownInputsBlock = (fields: DropdownInput[] | undefined) => {
    const wrapWithAfterDropdown = (field: ReactNode, variableName: string) => {
      const afterDropdown = renderAfterDropdown?.(variableName)
      if (!afterDropdown) {
        return field
      }
      return (
        <Fragment key={variableName}>
          {field}
          {afterDropdown}
        </Fragment>
      )
    }

    return fields?.map((input) => {
      if (input.button) {
        const fetchDropdown = (
          <MountingInputFieldWrapper
            key={input.variableName}
            input={input}
            systemUuid={system.uuid}
            mountingJson={mountingJson}
            mountingName={integrationName}
          >
            <MountingFetchDropdown
              input={input as FetchDropdownProps['input']}
              mountingName={integrationName}
              mountingJson={mountingJson}
              allowEdit={allowEdit}
              outlineWarningFieldNames={outlineWarningFieldNames}
            />
          </MountingInputFieldWrapper>
        )
        return wrapWithAfterDropdown(fetchDropdown, input.variableName)
      }

      const inputValue =
        typeof mountingJson !== 'undefined' && typeof mountingJson[input.variableName] !== 'undefined'
          ? mountingJson[input.variableName]
          : input.default

      const dropdownFieldWrapped = (
        <MountingInputFieldWrapper
          key={input.variableName}
          input={input}
          systemUuid={system.uuid}
          mountingJson={mountingJson}
          mountingName={integrationName}
        >
          <div
            className={
              isOutlineWarningField(input.variableName, outlineWarningFieldNames)
                ? staleInputBorderClasses.staleInputBorder
                : undefined
            }
          >
            <CustomSelectField
              className={'mounting-dropdown-input'}
              id={`${input.variableName}`}
              label={
                input.tooltip ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {translate(input.label)}
                    <Tooltip
                      title={
                        // title type = ReactNode, message only works with strings
                        <span style={{ fontWeight: 400 }}>
                          {renderMountingTooltipMessage(input.tooltip, translate, translateParse)}
                        </span>
                      }
                      placement="top"
                    >
                      <InfoOutlined style={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                    </Tooltip>
                  </span>
                ) : (
                  translate(input.label)
                )
              }
              value={inputValue}
              style={
                isVanDerValkMountingName(integrationName)
                  ? mountingVanDerValkDropdownStyle()
                  : { flex: '1 1', width: 230 }
              }
              disabled={!allowEdit}
              onChange={(event) => {
                const newValue = event.target.value
                handleApplyMountingSetting(
                  input.variableName,
                  newValue,
                  window.editor.selectedSystem.uuid,
                  integrationName
                )
                const handler = getDropdownChangeHandler(integrationName, input.variableName, newValue)
                if (handler) handler(window.editor.selectedSystem, translate)
              }}
            >
              {input.options.map((option) => (
                <MenuItem
                  key={option.value as any}
                  value={option.value as any}
                  className={'mounting-dropdown-option'}
                  id={`${option.value}`}
                >
                  {typeof option.label === 'string' ? translate(option.label) : option.label}
                </MenuItem>
              ))}
            </CustomSelectField>
          </div>
        </MountingInputFieldWrapper>
      )

      return wrapWithAfterDropdown(dropdownFieldWrapped, input.variableName)
    })
  }

  const renderTextFieldInputsBlock = (fields: TextFieldInput[] | undefined, keyPrefix = '') =>
    fields?.map((input, index) => (
      <Fragment key={`${keyPrefix}${input.variableName}_${index}`}>
        <MountingInputFieldWrapper
          input={input}
          systemUuid={system.uuid}
          mountingJson={mountingJson}
          mountingName={integrationName}
        >
          <MountingTextField
            input={input}
            mountingName={integrationName}
            mountingJson={mountingJson}
            allowEdit={allowEdit}
            outlineWarningFieldNames={outlineWarningFieldNames}
          />
        </MountingInputFieldWrapper>
        {renderAfterTextField?.(input.variableName)}
      </Fragment>
    ))

  if (hasMountingError) {
    return null
  }

  return (
    <>
      {renderDropdownInputsBlock(dropdownInputs)}
      {renderTextFieldInputsBlock(textFieldInputs)}
      {renderDropdownInputsBlock(trailingDropdownInputs)}
      {renderTextFieldInputsBlock(trailingTextFieldInputs, 'trailing_')}
      {renderFooter}

      {integrationName === 'solarNestRooftop' &&
        (() => {
          const maxes = getSolarNestMaxWindSnow(system)
          if (!maxes) return null
          const currentWind = Number(mountingJson?.windSpeed)
          const currentSnow = Number(mountingJson?.designSnowLoad)
          const windAboveMax =
            typeof currentWind === 'number' && !Number.isNaN(currentWind) && currentWind > maxes.maxWindMph
          const snowAboveMax =
            typeof currentSnow === 'number' && !Number.isNaN(currentSnow) && currentSnow > maxes.maxSnowPsf
          if (!windAboveMax && !snowAboveMax) return null
          return (
            <Alert severity="info" style={{ fontSize: '0.85em', padding: 10, marginTop: 15 }}>
              {translate(
                "This location's wind and snow zones are above the maximum for this system and requires structural analysis, please contact PES/Nuance"
              )}
            </Alert>
          )
        })()}
    </>
  )
}

const MountingInputFieldWrapper = ({
  children,
  input,
  systemUuid,
  mountingJson,
  mountingName,
}: {
  children: React.ReactNode
  input: (DropdownInput | TextFieldInput) &
    Partial<Extract<DropdownInput & TextFieldInput, DropdownInput | TextFieldInput>>
  systemUuid: string
  mountingJson: any
  mountingName: NativeMountingID | PartnerAccessoriesID
}): JSX.Element => {
  useEffect(() => {
    if (mountingJson && mountingJson[input.variableName] !== undefined) {
      if (!input.options?.length) {
        return
      }
      const matchingOption = input.options?.filter((option) => option.value === mountingJson[input.variableName])
      if (matchingOption?.length) {
        return
      }
    }
    handleApplyMountingSetting(input.variableName, input.default, systemUuid, mountingName)
  }, [input.variableName, input.default])
  return <>{children}</>
}

export const MountingInputs: FC<MountingInputsProps> = ({
  mountingName,
  allowEdit,
  afterAutoRacking,
  hideInputFields = false,
  FieldsComponent,
}) => {
  const nativeMountingSystems = getNativeMountingSystems()
  const form = useForm()
  const system = window.editor.selectedSystem
  const inputs = nativeMountingSystems[mountingName]?.getInputs(form, system)
  const translate = useTranslate()
  const classes = useStyles()
  const mountingJson = system?.integration_json ? system?.integration_json[mountingName] : undefined
  const autoRackingEnabled = typeof mountingJson?.enabled !== 'undefined' ? mountingJson.enabled : false

  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'mounting-inputs',
    eventName: 'Mounting Inputs',
    description: 'Tracking mounting inputs interactions',
  })

  const Fields = FieldsComponent ?? NativeMountingInputsFields

  return (
    <>
      <div className={classes.switchWrapper}>
        <div>{translate('Auto-Racking')}</div>
        <div id="auto-racking-toggle-container">
          <Switch
            checked={autoRackingEnabled}
            disabled={!allowEdit}
            id="auto-racking-toggle"
            onChange={(e) => {
              // Track auto-racking toggle
              trackUserInteraction(
                { type: ActionType.TOGGLE },
                {
                  eventName: e.target.checked ? 'Mounting Auto Racking Enabled' : 'Mounting Auto Racking Disabled',
                  systemId: window.editor.selectedSystem.uuid,
                  mountingName: mountingName,
                }
              )

              handleApplyMountingSetting('enabled', e.target.checked, window.editor.selectedSystem.uuid, mountingName)
            }}
          />
          {autoRackingEnabled ? translate('On') : translate('Off')}
        </div>
      </div>
      {afterAutoRacking}
      {!hideInputFields && (
        <Fields inputs={inputs} mountingJson={mountingJson} integrationName={mountingName} allowEdit={allowEdit} />
      )}
    </>
  )
}

export const PartnerAccessoryInputs: FC<PartnerAccessoryInputsProps> = ({ partnerAccessory, allowEdit }) => {
  const form = useForm()
  const system = window.editor.selectedSystem
  const inputs = partnerAccessories[partnerAccessory]?.getInputs(form, system)
  const mountingJson = system?.integration_json ? system?.integration_json[partnerAccessory] : undefined

  return (
    <NativeMountingInputsFields
      inputs={inputs}
      mountingJson={mountingJson}
      integrationName={partnerAccessory}
      allowEdit={allowEdit}
    />
  )
}

type TextFieldProps = {
  input: TextFieldInput
  mountingName: NativeMountingID | PartnerAccessoriesID
  mountingJson: IntegrationData | undefined
  allowEdit: boolean
  fullWidth?: boolean
  outlineWarningFieldNames?: readonly string[]
}

const applyValidatedMountingSetting = (
  variableName: string,
  value: any,
  mountingName: string,
  validateMountingSystem?: (mountingName: string) => boolean
): boolean => {
  if (!mountingName) return false
  if (validateMountingSystem && !validateMountingSystem(mountingName)) return false

  const nativeMountingSystems = getNativeMountingSystems()
  if (!nativeMountingSystems[mountingName as NativeMountingID]) return false

  const system = window.editor.selectedSystem
  handleApplyMountingSetting(variableName, value, system.uuid, mountingName as NativeMountingID)
  return true
}

function applyCompanionFields(
  fetchConfig: MountingFieldFetchConfig | undefined,
  mountingName: string | undefined,
  validateMountingSystem?: (mountingName: string) => boolean
): void {
  const companions = fetchConfig?.companionApplyFields
  if (!companions?.length || !mountingName) return

  companions.forEach((companion) => {
    const cached = companion.getCacheData()
    if (!cached?.success || cached.value == null) return
    applyValidatedMountingSetting(companion.variableName, cached.value, mountingName, validateMountingSystem)
  })
}

type TryApplyFromCachedDataOptions = {
  silent?: boolean
  mapResultToValue?: (value: unknown) => unknown
}

const tryApplyFromCachedData = (
  input: Pick<TextFieldInput, 'variableName' | 'button'>,
  updateFieldValue: (value: any) => void,
  validateMountingSystem?: (mountingName: string) => boolean,
  options?: TryApplyFromCachedDataOptions
): boolean => {
  const fetchConfiguration = input.button?.fetchConfig
  if (!fetchConfiguration?.getCacheData) return false

  const cachedData = fetchConfiguration.getCacheData()
  if (!cachedData || !cachedData.success || cachedData.value === null) return false

  const mapFn = options?.mapResultToValue ?? ((value: unknown) => value)
  const mappedValue = mapFn(cachedData.value)

  const currentSystem = window.editor.selectedSystem
  const currentMountingName = currentSystem.mounting

  if (!currentMountingName) return false
  if (!applyValidatedMountingSetting(input.variableName, mappedValue, currentMountingName, validateMountingSystem))
    return false

  updateFieldValue(mappedValue)
  applyCompanionFields(fetchConfiguration, currentMountingName, validateMountingSystem)
  if (!options?.silent) {
    window.Designer?.showNotification('Data successfully applied!', 'success')
  }
  return true
}

type MountingFieldAutoFetchParams = {
  allowEdit: boolean
  mountingName: NativeMountingID | PartnerAccessoriesID
  variableName: string
  fetchConfiguration: NonNullable<TextFieldInput['button']>['fetchConfig']
  fieldValue: unknown
  fieldDefault?: InputValue
  setFieldValue: (value: any) => void
  isValueFromExternalCache: () => boolean
  hasCachedDataButDifferentValue: () => boolean
  mapResultToValue?: (value: unknown) => unknown
}

function isMountingFetchFieldUnsetOrPlaceholder(
  fieldValue: unknown,
  fieldDefault: unknown,
  fallbackValue: unknown
): boolean {
  if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
    return true
  }
  if (fieldDefault !== undefined && String(fieldValue) === String(fieldDefault)) {
    return true
  }
  if (fallbackValue !== undefined && String(fieldValue) === String(fallbackValue)) {
    return true
  }
  return false
}

function runMountingFieldAutoFetchOnMount(params: MountingFieldAutoFetchParams): void {
  const { fetchConfiguration, allowEdit, mountingName } = params
  if (!allowEdit || !fetchConfiguration) return

  const { isValidMountingSystem, fetchFunction, fallbackValue } = fetchConfiguration as {
    isValidMountingSystem?: (name: string) => boolean
    fetchFunction?: typeof fetchDlubalUKData
    fallbackValue?: unknown
  }
  if (isValidMountingSystem && !isValidMountingSystem(mountingName)) return

  const userLikelyEdited =
    params.hasCachedDataButDifferentValue() &&
    !isMountingFetchFieldUnsetOrPlaceholder(params.fieldValue, params.fieldDefault, fallbackValue)

  if (userLikelyEdited) return

  if (!params.isValueFromExternalCache()) {
    const applied = tryApplyFromCachedData(
      { variableName: params.variableName, button: { label: '', fetchConfig: fetchConfiguration } },
      params.setFieldValue,
      isValidMountingSystem,
      { silent: true, mapResultToValue: params.mapResultToValue }
    )
    if (applied) return
  } else {
    return
  }

  if (fetchFunction === fetchDlubalUKData) {
    requestAutoFetchDlubalUKDataIfNeeded()
  }
}

type FetchDataWithTimeoutOptions = {
  projectForm: FormApi
  input: TextFieldInput
  dataFetchFunction: () => Promise<void>
  cacheKey: string
  dataFieldKey: string
  timeoutDurationSeconds: number
  updateFieldValue: (value: any) => void
  validateMountingSystem?: (mountingName: string) => boolean
  dispatch: (action: any) => void
  manualRequestId?: string
}

/** Prefer fetchConfig.getCacheData (slope-aware for Fast Rack snow) over raw custom_data[resultKey]. */
const resolveFetchedValueForApply = (
  input: TextFieldInput,
  formCustomData: Record<string, unknown>,
  cacheKey: string,
  dataFieldKey: string
): { success: boolean; value: unknown } => {
  const fetchConfig = input.button?.fetchConfig as
    | { getCacheData?: () => { success: boolean; value: unknown } }
    | undefined
  const fromResolver = fetchConfig?.getCacheData?.()
  if (fromResolver?.success && fromResolver.value !== null && fromResolver.value !== undefined) {
    return { success: true, value: fromResolver.value }
  }

  const entry = formCustomData[cacheKey] as { success?: boolean; [key: string]: unknown } | undefined
  return {
    success: entry?.success === true,
    value: entry?.[dataFieldKey],
  }
}

const fetchDataWithTimeout = async (options: FetchDataWithTimeoutOptions): Promise<void> => {
  const {
    projectForm,
    input,
    dataFetchFunction,
    cacheKey,
    dataFieldKey,
    timeoutDurationSeconds,
    updateFieldValue,
    validateMountingSystem,
    dispatch,
    manualRequestId,
  } = options
  const dispatchCompletionEvent = async () => {
    try {
      await dataFetchFunction()
      if (!manualRequestId) return

      // Prefer getCacheData (e.g. slope-resolved snow) over raw custom_data[resultKey] snapshot.
      const formCustomData = projectForm?.getState().values.custom_data || {}
      const { success, value } = resolveFetchedValueForApply(input, formCustomData, cacheKey, dataFieldKey)

      dispatch({
        type: 'MOUNTING_AUTO_FETCH_COMPLETE',
        dataKey: cacheKey,
        resultKey: dataFieldKey,
        requestId: manualRequestId,
        success,
        cachedData: value,
      })
    } catch (error) {
      if (!manualRequestId) throw error

      dispatch({
        type: 'MOUNTING_AUTO_FETCH_COMPLETE',
        dataKey: cacheKey,
        resultKey: dataFieldKey,
        requestId: manualRequestId,
        success: false,
      })
      throw error
    }
  }

  const timeoutError = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Timeout')), timeoutDurationSeconds * 1000)
  })

  await Promise.race([dispatchCompletionEvent(), timeoutError])

  // Apply resolved display value (getCacheData when provided, else custom_data[resultKey]).
  const formCustomData = projectForm?.getState().values.custom_data || {}
  const { success, value } = resolveFetchedValueForApply(input, formCustomData, cacheKey, dataFieldKey)
  if (!success) {
    throw new Error('No data received')
  }

  const currentSystem = window.editor.selectedSystem
  const currentMountingName = currentSystem.mounting

  if (!currentMountingName) return
  if (!applyValidatedMountingSetting(input.variableName, value, currentMountingName, validateMountingSystem)) return

  updateFieldValue(value)
  applyCompanionFields(input.button?.fetchConfig, currentMountingName, validateMountingSystem)
  window.Designer?.showNotification('Data successfully fetched and applied!', 'success')
}

type FetchDropdownProps = {
  input: DropdownInput & { button: NonNullable<DropdownInput['button']> }
  mountingName: NativeMountingID | PartnerAccessoriesID
  mountingJson: IntegrationData | undefined
  allowEdit: boolean
  outlineWarningFieldNames?: readonly string[]
}

const MountingFetchDropdown: FC<FetchDropdownProps> = ({
  input,
  mountingName,
  mountingJson,
  allowEdit,
  outlineWarningFieldNames,
}) => {
  const translate = useTranslate()
  const form = useForm()
  const dispatch = useDispatch()
  const staleInputBorderClasses = useMountingStaleInputBorderStyles()
  const [fieldValue, setFieldValue] = useState(
    typeof mountingJson !== 'undefined' && typeof mountingJson[input.variableName] !== 'undefined'
      ? mountingJson[input.variableName]
      : input.default
  )
  const [isAwaitingData, setIsAwaitingData] = useState(false)
  const [remainingTimeoutSeconds, setRemainingTimeoutSeconds] = useState(Infinity)
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null)

  const fetchConfiguration = input.button?.fetchConfig
  const mapFn = (fetchConfiguration as any)?.mapResultToValue ?? ((v: any) => v)

  const isValueFromExternalCache = () => {
    if (!fetchConfiguration) return false
    const { getCacheData } = fetchConfiguration as any
    const cachedData = getCacheData()
    if (!cachedData || !cachedData.success || cachedData.value === null) return false
    return String(fieldValue) === String(mapFn(cachedData.value))
  }

  const hasCachedDataButDifferentValue = () => {
    if (!fetchConfiguration) return false
    const { getCacheData } = fetchConfiguration as any
    const cachedData = getCacheData()
    if (!cachedData || !cachedData.success || cachedData.value === null) return false
    return String(fieldValue) !== String(mapFn(cachedData.value))
  }

  const hasFetchFailed = () => {
    if (!fetchConfiguration) return false
    const { getCacheData } = fetchConfiguration as any
    const cachedData = getCacheData()
    return cachedData && cachedData.success === false
  }

  const actionListener = useCallback(
    (action: any) => {
      if (!fetchConfiguration) return

      if (action?.type === 'MOUNTING_AUTO_FETCH_START' && action.dataKey === fetchConfiguration.dataKey) {
        const cfgResultKey = (fetchConfiguration as { resultKey?: string }).resultKey
        if (action.resultKey != null && cfgResultKey != null && action.resultKey !== cfgResultKey) {
          return
        }
        setTimeout(() => {
          if (!isAwaitingData) {
            setIsAwaitingData(true)
            setRemainingTimeoutSeconds(fetchConfiguration.timeoutSeconds)
            setActiveRequestId(action.requestId)
          } else {
            setActiveRequestId(action.requestId)
          }
        }, 0)
      }

      if (action?.type === 'MOUNTING_AUTO_FETCH_COMPLETE' && action.dataKey === fetchConfiguration.dataKey) {
        setTimeout(() => {
          const cfgResultKey = (fetchConfiguration as { resultKey?: string }).resultKey
          if (action.resultKey != null && cfgResultKey != null && action.resultKey !== cfgResultKey) {
            return
          }
          if (activeRequestId != null && action.requestId !== activeRequestId) return

          setIsAwaitingData(false)
          setRemainingTimeoutSeconds(fetchConfiguration.timeoutSeconds)
          setActiveRequestId(null)

          if (!action.success || !action.cachedData) return

          const mappedValue = mapFn(action.cachedData)
          const currentSystem = window.editor.selectedSystem
          const currentMountingName = currentSystem.mounting
          if (!currentMountingName) return

          if (
            !applyValidatedMountingSetting(
              input.variableName,
              mappedValue,
              currentMountingName,
              fetchConfiguration.isValidMountingSystem
            )
          )
            return

          setFieldValue(mappedValue)
          applyCompanionFields(fetchConfiguration, currentMountingName, fetchConfiguration.isValidMountingSystem)
        }, 0)
      }
    },
    [input.button?.fetchConfig, input.variableName, activeRequestId, isAwaitingData]
  )

  useActionsListener(actionListener)

  useEffect(() => {
    if (!fetchConfiguration) return
    runMountingFieldAutoFetchOnMount({
      allowEdit,
      mountingName,
      variableName: input.variableName,
      fetchConfiguration,
      fieldValue,
      fieldDefault: input.default,
      setFieldValue,
      isValueFromExternalCache,
      hasCachedDataButDifferentValue,
      mapResultToValue: mapFn,
    })
    // Mount-only: apply cached Dlubal data or kick off background fetch when field first renders.
  }, [])

  useEffect(() => {
    let countdownTimer: NodeJS.Timeout

    if (isAwaitingData && remainingTimeoutSeconds > 0) {
      countdownTimer = setTimeout(() => setRemainingTimeoutSeconds(remainingTimeoutSeconds - 1), 1000)
      return () => clearTimeout(countdownTimer)
    }

    if (!isAwaitingData || remainingTimeoutSeconds !== 0) return

    setIsAwaitingData(false)
    if (fetchConfiguration) {
      setRemainingTimeoutSeconds(fetchConfiguration.timeoutSeconds)
    }
    setActiveRequestId(null)

    if (fetchConfiguration?.fallbackValue !== undefined) {
      const currentSystem = window.editor.selectedSystem
      const currentMountingName = currentSystem.mounting

      if (
        currentMountingName &&
        applyValidatedMountingSetting(
          input.variableName,
          fetchConfiguration.fallbackValue,
          currentMountingName,
          fetchConfiguration.isValidMountingSystem
        )
      ) {
        setFieldValue(fetchConfiguration.fallbackValue)
      }
    }

    const timeoutMessage = fetchConfiguration?.timeoutErrorMessage || 'Data fetch timed out. Please try again.'
    window.Designer?.showNotification(timeoutMessage, 'error')
  }, [isAwaitingData, remainingTimeoutSeconds, fetchConfiguration?.timeoutSeconds, activeRequestId])

  const executeDataFetch = async () => {
    if (!fetchConfiguration) return
    const { dataKey, resultKey, fetchFunction, timeoutSeconds, isValidMountingSystem } = fetchConfiguration

    try {
      const cachedResult = (fetchConfiguration as any).getCacheData?.()
      if (cachedResult?.success && cachedResult.value !== null) {
        const mappedValue = mapFn(cachedResult.value)
        const currentMountingName = window.editor.selectedSystem.mounting
        if (
          currentMountingName &&
          applyValidatedMountingSetting(input.variableName, mappedValue, currentMountingName, isValidMountingSystem)
        ) {
          setFieldValue(mappedValue)
          applyCompanionFields(fetchConfiguration, currentMountingName, isValidMountingSystem)
          window.Designer?.showNotification('Data successfully applied!', 'success')
          return
        }
      }

      const manualFetchRequestId = generateUUID()
      setIsAwaitingData(true)
      setRemainingTimeoutSeconds(timeoutSeconds)
      setActiveRequestId(manualFetchRequestId)

      dispatch({
        type: 'MOUNTING_AUTO_FETCH_START',
        dataKey,
        resultKey,
        requestId: manualFetchRequestId,
      })

      await fetchDataWithTimeout({
        projectForm: form,
        input: { ...input, type: 'number' } as any,
        dataFetchFunction: fetchFunction,
        cacheKey: dataKey,
        dataFieldKey: resultKey,
        timeoutDurationSeconds: timeoutSeconds,
        updateFieldValue: (rawValue: any) => setFieldValue(mapFn(rawValue)),
        validateMountingSystem: isValidMountingSystem,
        dispatch,
        manualRequestId: manualFetchRequestId,
      })

      setIsAwaitingData(false)
      setRemainingTimeoutSeconds(timeoutSeconds)
      setActiveRequestId(null)
    } catch (error: any) {
      setIsAwaitingData(false)
      setRemainingTimeoutSeconds(timeoutSeconds)
      setActiveRequestId(null)

      if (fetchConfiguration.fallbackValue !== undefined) {
        const currentMountingName = window.editor.selectedSystem.mounting
        if (
          currentMountingName &&
          applyValidatedMountingSetting(
            input.variableName,
            fetchConfiguration.fallbackValue,
            currentMountingName,
            isValidMountingSystem
          )
        ) {
          setFieldValue(fetchConfiguration.fallbackValue)
        }
      }

      const errorMessage = getMountingFetchErrorMessage(error, fetchConfiguration)
      window.Designer?.showNotification(errorMessage, 'error')
    }
  }

  const isFromCache = isValueFromExternalCache()
  const hasChangedFromCache = hasCachedDataButDifferentValue() && !isAwaitingData
  const fetchHasFailed = hasFetchFailed()
  const cacheTooltipText =
    (fetchConfiguration as any)?.cacheIndicatorTooltip || 'Value automatically populated from external data source'
  const changedFromCacheTooltip =
    (fetchConfiguration as any)?.changedFromCacheTooltip ||
    'You have changed values from the calculated values from external data source.'
  const showCacheIndicator = isFromCache || hasChangedFromCache || fetchHasFailed

  const cacheIndicatorAdornment = showCacheIndicator ? (
    <InputAdornment position="end">
      <Tooltip
        title={
          isFromCache ? cacheTooltipText : hasChangedFromCache ? changedFromCacheTooltip : DEFAULT_FAILED_FETCH_TOOLTIP
        }
        placement="top"
      >
        {isFromCache ? (
          <CheckCircleOutline style={{ color: '#4CAF50', fontSize: 20 }} />
        ) : hasChangedFromCache ? (
          <InfoOutlined style={{ color: '#FF9800', fontSize: 20 }} />
        ) : (
          <InfoOutlined style={{ color: '#F44336', fontSize: 20 }} />
        )}
      </Tooltip>
    </InputAdornment>
  ) : undefined

  return (
    <div style={mountingFetchDropdownContainerStyle(mountingName)}>
      <div
        className={
          isOutlineWarningField(input.variableName, outlineWarningFieldNames)
            ? staleInputBorderClasses.staleInputBorder
            : undefined
        }
      >
        <CustomSelectField
          className={'mounting-dropdown-input'}
          id={`${input.variableName}`}
          label={translate(input.label)}
          value={fieldValue}
          style={mountingFetchDropdownFieldStyle(mountingName)}
          disabled={!allowEdit || isAwaitingData}
        onChange={(event) => {
          const newValue = event.target.value
          setFieldValue(newValue)
          handleApplyMountingSetting(input.variableName, newValue, window.editor.selectedSystem.uuid, mountingName)
        }}
        InputProps={{
          endAdornment: cacheIndicatorAdornment,
        }}
      >
        {input.options.map((option) => (
          <MenuItem
            key={option.value as any}
            value={option.value as any}
            className={'mounting-dropdown-option'}
            id={`${option.value}`}
          >
            {typeof option.label === 'string' ? translate(option.label) : option.label}
          </MenuItem>
        ))}
      </CustomSelectField>
      </div>
      {input.button && allowEdit && !isFromCache && (
        <Button
          size="small"
          onClick={executeDataFetch}
          disabled={input.button.disabled || isAwaitingData}
          style={mountingTextFieldButtonStyle(mountingName)}
          startIcon={<SyncIcon />}
        >
          {isAwaitingData
            ? `${remainingTimeoutSeconds}s...`
            : hasChangedFromCache
              ? translate('Reset')
              : translate(input.button.label)}
        </Button>
      )}
    </div>
  )
}

const MountingTextField: FC<TextFieldProps> = ({
  input,
  mountingName,
  mountingJson,
  allowEdit,
  fullWidth = false,
  outlineWarningFieldNames,
}) => {
  const translate = useTranslate()
  const form = useForm()
  const dispatch = useDispatch()
  const staleInputBorderClasses = useMountingStaleInputBorderStyles()
  const [fieldValue, setFieldValue] = useState(
    typeof mountingJson !== 'undefined' && typeof mountingJson[input.variableName] !== 'undefined'
      ? mountingJson[input.variableName]
      : input.default
  )
  const [isAwaitingData, setIsAwaitingData] = useState(false)
  const [remainingTimeoutSeconds, setRemainingTimeoutSeconds] = useState(Infinity)
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null)
  const [maxSlope, setMaxSlope] = useState<number | null>(null)

  // Sync text field display when mountingJson is updated externally (e.g. SolarPort portrait
  // resets arraySpacing; UK Dlubal sets vanDerValk.altitudeAboveSea / distanceToShorelineKM after load zones fetch).
  useEffect(() => {
    const externalValue = mountingJson?.[input.variableName]
    if (externalValue !== undefined && String(externalValue) !== String(fieldValue)) {
      setFieldValue(externalValue)
    }
    // eslint-disable-next-line react-app/react-hooks/exhaustive-deps
  }, [mountingJson?.[input.variableName], mountingName, input.variableName])

  const fetchConfiguration = input.button?.fetchConfig

  const isValueFromExternalCache = () => {
    if (!fetchConfiguration?.getCacheData) return false

    const cachedData = fetchConfiguration.getCacheData()

    if (!cachedData || !cachedData.success || cachedData.value === null) return false
    return String(fieldValue) === String(cachedData.value)
  }

  const hasCachedDataButDifferentValue = () => {
    if (!fetchConfiguration?.getCacheData) return false

    const cachedData = fetchConfiguration.getCacheData()

    if (!cachedData || !cachedData.success || cachedData.value === null) return false
    return String(fieldValue) !== String(cachedData.value)
  }

  const hasFetchFailed = () => {
    if (!fetchConfiguration?.getCacheData) return false

    const cachedData = fetchConfiguration.getCacheData()

    return cachedData && cachedData.success === false
  }

  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'mounting-text-field',
    eventName: 'Mounting Text Field',
    description: 'Tracking mounting text field interactions',
  })

  const applyDebouncedValueChange = useDebouncedCallback((newValue) => {
    if (input.type === 'number') {
      if (input.min !== undefined && Number(newValue) < input.min) {
        newValue = input.min.toString()
      } else if (input.max !== undefined && Number(newValue) > input.max) {
        newValue = input.max.toString()
      }
      if (input.maxDecimals !== undefined && newValue !== '') {
        const rounded = parseFloat(Number(newValue).toFixed(input.maxDecimals))
        newValue = isNaN(rounded) ? newValue : rounded.toString()
      }
      setFieldValue(newValue)
    }
    handleApplyMountingSetting(input.variableName, newValue, window.editor.selectedSystem.uuid, mountingName)
  }, 500)

  const actionListener = useCallback(
    (action: any) => {
      if (!fetchConfiguration) return

      if (action?.type === 'MOUNTING_AUTO_FETCH_START' && action.dataKey === fetchConfiguration.dataKey) {
        const cfgResultKey = (fetchConfiguration as { resultKey?: string }).resultKey
        if (action.resultKey != null && cfgResultKey != null && action.resultKey !== cfgResultKey) {
          return
        }
        setTimeout(() => {
          if (!isAwaitingData) {
            setIsAwaitingData(true)
            setRemainingTimeoutSeconds(fetchConfiguration.timeoutSeconds)
            setActiveRequestId(action.requestId)
          } else {
            setActiveRequestId(action.requestId)
          }
        }, 0)
      }

      if (
        action?.type === 'MOUNTING_AUTO_FETCH_COMPLETE' &&
        action.dataKey === fetchConfiguration.dataKey &&
        action.resultKey === fetchConfiguration.resultKey
      ) {
        setTimeout(() => {
          const cfgResultKey = (fetchConfiguration as { resultKey?: string }).resultKey
          if (action.resultKey != null && cfgResultKey != null && action.resultKey !== cfgResultKey) {
            return
          }
          if (activeRequestId != null && action.requestId !== activeRequestId) return

          setIsAwaitingData(false)
          if (fetchConfiguration.timeoutSeconds) {
            setRemainingTimeoutSeconds(fetchConfiguration.timeoutSeconds)
          }
          setActiveRequestId(null)

          if (!action.success || action.cachedData === null || action.cachedData === undefined) {
            return
          }

          const currentSystem = window.editor.selectedSystem
          const currentMountingName = currentSystem.mounting
          if (!currentMountingName) return
          if (
            !applyValidatedMountingSetting(
              input.variableName,
              action.cachedData,
              currentMountingName,
              fetchConfiguration.isValidMountingSystem
            )
          )
            return

          setFieldValue(action.cachedData)
        }, 0)
      }
    },
    [fetchConfiguration, input.variableName, activeRequestId, isAwaitingData]
  )

  useActionsListener(actionListener)

  useEffect(() => {
    const fetchConfiguration = input.button?.fetchConfig
    if (!fetchConfiguration) return
    runMountingFieldAutoFetchOnMount({
      allowEdit,
      mountingName,
      variableName: input.variableName,
      fetchConfiguration,
      fieldValue,
      fieldDefault: input.default,
      setFieldValue,
      isValueFromExternalCache,
      hasCachedDataButDifferentValue,
    })
    // Mount-only: apply cached Dlubal data or kick off background fetch when field first renders.
  }, [])

  useEffect(() => {
    let countdownTimer: NodeJS.Timeout

    if (isAwaitingData && remainingTimeoutSeconds > 0) {
      countdownTimer = setTimeout(() => setRemainingTimeoutSeconds(remainingTimeoutSeconds - 1), 1000)
      return () => clearTimeout(countdownTimer)
    }

    if (!isAwaitingData || remainingTimeoutSeconds !== 0) return

    setIsAwaitingData(false)
    if (fetchConfiguration) {
      setRemainingTimeoutSeconds(fetchConfiguration.timeoutSeconds)
    }
    setActiveRequestId(null)

    if (fetchConfiguration?.fallbackValue !== undefined) {
      const currentSystem = window.editor.selectedSystem
      const currentMountingName = currentSystem.mounting

      if (
        currentMountingName &&
        applyValidatedMountingSetting(
          input.variableName,
          fetchConfiguration.fallbackValue,
          currentMountingName,
          fetchConfiguration.isValidMountingSystem
        )
      ) {
        setFieldValue(fetchConfiguration.fallbackValue)
      }
    }

    const timeoutMessage = fetchConfiguration?.timeoutErrorMessage || 'Data fetch timed out. Please try again.'
    window.Designer?.showNotification(timeoutMessage, 'error')
  }, [isAwaitingData, remainingTimeoutSeconds, input.button?.fetchConfig?.timeoutSeconds, activeRequestId])

  const executeDataFetch = async (fetchConfiguration: {
    dataKey: string
    resultKey: string
    fetchFunction: () => Promise<void>
    timeoutSeconds: number
    isValidMountingSystem?: (mountingName: string) => boolean
    fallbackValue?: any
    timeoutErrorMessage?: string
    getCacheData: () => { success: boolean; value: any }
  }) => {
    const { dataKey, resultKey, fetchFunction, timeoutSeconds, isValidMountingSystem } = fetchConfiguration

    try {
      const wasCachedDataApplied = tryApplyFromCachedData(input, setFieldValue, isValidMountingSystem)
      if (wasCachedDataApplied) return

      const manualFetchRequestId = generateUUID()

      setIsAwaitingData(true)
      setRemainingTimeoutSeconds(timeoutSeconds)
      setActiveRequestId(manualFetchRequestId)

      dispatch({
        type: 'MOUNTING_AUTO_FETCH_START',
        dataKey,
        resultKey,
        requestId: manualFetchRequestId,
      })

      await fetchDataWithTimeout({
        projectForm: form,
        input,
        dataFetchFunction: fetchFunction,
        cacheKey: dataKey,
        dataFieldKey: resultKey,
        timeoutDurationSeconds: timeoutSeconds,
        updateFieldValue: setFieldValue,
        validateMountingSystem: isValidMountingSystem,
        dispatch,
        manualRequestId: manualFetchRequestId,
      })

      setIsAwaitingData(false)
      setRemainingTimeoutSeconds(timeoutSeconds)
      setActiveRequestId(null)
    } catch (error: any) {
      setIsAwaitingData(false)
      setRemainingTimeoutSeconds(timeoutSeconds)
      setActiveRequestId(null)

      const shouldApplyFallback = fetchConfiguration.fallbackValue !== undefined
      if (shouldApplyFallback) {
        const currentSystem = window.editor.selectedSystem
        const currentMountingName = currentSystem.mounting

        if (
          currentMountingName &&
          applyValidatedMountingSetting(
            input.variableName,
            fetchConfiguration.fallbackValue,
            currentMountingName,
            isValidMountingSystem
          )
        ) {
          setFieldValue(fetchConfiguration.fallbackValue)
        }
      }

      const errorMessage = getMountingFetchErrorMessage(error, fetchConfiguration)
      window.Designer?.showNotification(errorMessage, 'error')
    }
  }

  const triggerButtonAction = async () => {
    if (input.button?.fetchConfig) {
      await executeDataFetch(input.button.fetchConfig)
    }
  }

  const isFromCache = isValueFromExternalCache()
  const hasChangedFromCache = hasCachedDataButDifferentValue() && !isAwaitingData
  const fetchHasFailed = hasFetchFailed()
  const cacheTooltipText =
    fetchConfiguration?.cacheIndicatorTooltip || 'Value automatically populated from external data source'
  const changedFromCacheTooltip =
    fetchConfiguration?.changedFromCacheTooltip ||
    'You have changed values from the calculated values from external data source.'

  useEffect(() => {
    if (input.variableName !== 'groundSlope' || mountingName !== 'solarNestGroundMount') {
      setMaxSlope(null)
      return
    }

    const calculateMaxSlope = () => {
      try {
        const system = window.editor?.selectedSystem
        if (!system) {
          setMaxSlope(null)
          return
        }
        const grids = system.moduleGrids()
        if (grids.length > 0) {
          const firstGrid = grids[0]
          const { numRows } = getPanelLayoutDimensions(firstGrid.cellsActive)
          const calculatedMaxSlope = numRows >= 10 ? 1 : 2
          setMaxSlope(calculatedMaxSlope)
        } else {
          setMaxSlope(null)
        }
      } catch (error) {
        setMaxSlope(null)
      }
    }
    calculateMaxSlope()

    const intervalId = setInterval(() => {
      calculateMaxSlope()
    }, 500)

    return () => {
      clearInterval(intervalId)
    }
  }, [input.variableName, mountingName, fieldValue])
  const showGroundSlopeTooltip = maxSlope !== null
  const showGroundClearanceTooltip = input.variableName === 'groundClearance' && mountingName === 'solarNestGroundMount'
  const showCacheIndicator = isFromCache || hasChangedFromCache || fetchHasFailed
  const showAnyTooltip = showGroundSlopeTooltip || showGroundClearanceTooltip || showCacheIndicator
  const isStaleInput = isOutlineWarningField(input.variableName, outlineWarningFieldNames)

  return (
    <div
      style={mountingTextFieldContainerStyle(mountingName, fullWidth)}
      className={isStaleInput ? staleInputBorderClasses.staleInputBorder : undefined}
    >
      <TextField
        type={input.type}
        label={translate(input.label)}
        value={fieldValue}
        style={mountingTextFieldStyle(mountingName, fullWidth)}
        fullWidth={fullWidth || usesCompactMountingTextFieldLayout(mountingName)}
        disabled={!allowEdit || isAwaitingData}
        onChange={(event) => {
          let newValue = event.target.value

          // Track mounting spacing changes
          if (input.variableName === 'mountingSpacing' || input.variableName === 'mountingSpacingInches') {
            trackUserInteraction(
              { type: ActionType.UPDATE },
              {
                eventName: 'Mounting Spacing Changed',
                systemId: window.editor.selectedSystem.uuid,
                mountingName: mountingName,
                spacingVariable: input.variableName,
                newValue: String(newValue),
                previousValue: String(fieldValue),
              }
            )
          }

          setFieldValue(newValue)
          applyDebouncedValueChange(newValue)
        }}
        InputProps={{
          endAdornment: showAnyTooltip && (
            <InputAdornment position="end" style={{ display: 'flex', gap: '4px' }}>
              {showGroundSlopeTooltip && (
                <Tooltip title={`Maximum slope for this system is ${maxSlope} degrees`} placement="top">
                  <InfoOutlined style={{ color: '#666', fontSize: 20, cursor: 'help' }} />
                </Tooltip>
              )}
              {showGroundClearanceTooltip && (
                <Tooltip title='Minimum ground clearance is 13" and maximum is 25"' placement="top">
                  <InfoOutlined style={{ color: '#666', fontSize: 20, cursor: 'help' }} />
                </Tooltip>
              )}
              {showCacheIndicator && (
                <Tooltip
                  title={
                    isFromCache
                      ? cacheTooltipText
                      : hasChangedFromCache
                        ? changedFromCacheTooltip
                        : DEFAULT_FAILED_FETCH_TOOLTIP
                  }
                  placement="top"
                >
                  {isFromCache ? (
                    <CheckCircleOutline style={{ color: '#4CAF50', fontSize: 20 }} />
                  ) : hasChangedFromCache ? (
                    <InfoOutlined style={{ color: '#FF9800', fontSize: 20 }} />
                  ) : (
                    <InfoOutlined style={{ color: '#F44336', fontSize: 20 }} />
                  )}
                </Tooltip>
              )}
            </InputAdornment>
          ),
        }}
      />
      {input.button && allowEdit && !isFromCache && (
        <Button
          size="small"
          onClick={triggerButtonAction}
          disabled={input.button.disabled || isAwaitingData}
          style={mountingTextFieldButtonStyle(mountingName, fullWidth)}
          startIcon={<SyncIcon />}
        >
          {isAwaitingData
            ? `${remainingTimeoutSeconds}s...`
            : hasChangedFromCache
              ? translate('Reset')
              : translate(input.button.label)}
        </Button>
      )}
    </div>
  )
}

export type MountingTextFieldInputProps = TextFieldProps & {
  systemUuid: string
}

export const MountingTextFieldInput: FC<MountingTextFieldInputProps> = ({
  input,
  mountingName,
  mountingJson,
  allowEdit,
  systemUuid,
  fullWidth = false,
  outlineWarningFieldNames,
}) => (
  <MountingInputFieldWrapper
    input={input}
    systemUuid={systemUuid}
    mountingJson={mountingJson}
    mountingName={mountingName}
  >
    <MountingTextField
      input={input}
      mountingName={mountingName}
      mountingJson={mountingJson}
      allowEdit={allowEdit}
      fullWidth={fullWidth}
      outlineWarningFieldNames={outlineWarningFieldNames}
    />
  </MountingInputFieldWrapper>
)
